import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormHelperText,
  Divider
} from '@mui/material';

interface BusinessVerificationStepProps {
  data: any;
  onChange: (data: any) => void;
}

const venueTypes = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Café' },
  { value: 'bar', label: 'Bar or Pub' },
  { value: 'event_space', label: 'Event Space' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'resort', label: 'Resort' },
  { value: 'private_venue', label: 'Private Venue' },
  { value: 'other', label: 'Other' }
];

const BusinessVerificationStep: React.FC<BusinessVerificationStepProps> = ({
  data,
  onChange
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onChange({
      [name]: value
    });
  };
  
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onChange({
      businessAddress: {
        ...data.businessAddress,
        [name]: value
      }
    });
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Business Information
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Please provide your business details for verification purposes. This information helps us verify that you are a legitimate venue and enables us to set up your payments.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={8}>
          <TextField
            required
            fullWidth
            label="Business Name"
            name="businessName"
            value={data.businessName}
            onChange={handleChange}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            required
            fullWidth
            select
            label="Business Type"
            name="businessType"
            value={data.businessType}
            onChange={handleChange}
          >
            {venueTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" gutterBottom>
            Business Address
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Street Address"
            name="street"
            value={data.businessAddress.street}
            onChange={handleAddressChange}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="City"
            name="city"
            value={data.businessAddress.city}
            onChange={handleAddressChange}
          />
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <TextField
            required
            fullWidth
            label="State/Province"
            name="state"
            value={data.businessAddress.state}
            onChange={handleAddressChange}
          />
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <TextField
            required
            fullWidth
            label="ZIP/Postal Code"
            name="zip"
            value={data.businessAddress.zip}
            onChange={handleAddressChange}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Country"
            name="country"
            value={data.businessAddress.country}
            onChange={handleAddressChange}
          >
            <MenuItem value="US">United States</MenuItem>
            <MenuItem value="CA">Canada</MenuItem>
            <MenuItem value="GB">United Kingdom</MenuItem>
            <MenuItem value="AU">Australia</MenuItem>
            {/* Add more countries as needed */}
          </TextField>
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Tax ID / Business Number"
            name="taxId"
            value={data.taxId}
            onChange={handleChange}
            helperText="For U.S. businesses, enter your EIN (XX-XXXXXXX)"
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormHelperText>
            All business information is securely encrypted and used only for verification purposes. We comply with all applicable data protection regulations.
          </FormHelperText>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusinessVerificationStep;
